import type {
  CatalogDraft,
  InstallationSession,
  RefundSettings,
  TerminalDiagnostic,
  TerminalDiagnosticCode,
} from "@/client/types";
import { WorkerRequestError } from "@/lib/worker-error";

type ConfigResponse = {
  configured: boolean;
  environment?: "test" | "live";
  liveRegion?: "eu" | "us" | "au" | "apse" | null;
  merchantAccount?: string;
  terminalId?: string;
  saleId?: string;
  currency?: "EUR" | "USD" | "AUD";
  maxRefundMinor?: number;
  paymentMethodId?: string;
  webhookUrl?: string;
  apiKeyConfigured?: boolean;
  webhookHmacConfigured?: boolean;
};

type CatalogResponse = {
  items: Array<{
    sku: string;
    itemId: string;
    displayName: string;
    valueMinor: number;
    currency: string;
    enabled: boolean;
    sortOrder: number;
  }>;
};

export class WorkerApi {
  constructor(private readonly session: InstallationSession) {}

  static async bootstrap(): Promise<InstallationSession> {
    const response = await fetch("/v1/installations", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
    });
    const payload = await readJson<{
      installationId: string;
      installationSecret: string;
    }>(response);
    return {
      installationId: payload.installationId,
      installationSecret: payload.installationSecret,
    };
  }

  async getConfig(): Promise<RefundSettings | null> {
    const payload = await this.request<ConfigResponse>("config");
    if (!payload.configured) return null;
    return {
      environment: payload.environment ?? "test",
      region: normalizeRegion(payload.liveRegion),
      merchantAccount: payload.merchantAccount ?? "",
      terminalId: payload.terminalId ?? "",
      saleId: payload.saleId ?? "",
      currency: payload.currency ?? "EUR",
      maxRefundCents: payload.maxRefundMinor ?? 0,
      settlementRoute: payload.paymentMethodId ?? "",
      apiKey: "",
      webhookHmacKey: "",
      webhookUrl: payload.webhookUrl ?? "",
    };
  }

  async saveConfig(settings: RefundSettings): Promise<void> {
    await this.request("config", "PUT", configRequest(settings));
  }

  async saveSettings(
    settings: RefundSettings,
    items: CatalogDraft[],
  ): Promise<void> {
    await this.request("settings", "PUT", {
      config: configRequest(settings),
      catalog: catalogRequest(items),
    });
  }

  async getCatalog(): Promise<CatalogDraft[]> {
    const payload = await this.request<CatalogResponse>("catalog");
    return payload.items.map((item) => ({
      itemId: item.itemId,
      name: item.displayName,
      sku: item.sku,
      depositCents: item.valueMinor,
      enabled: item.enabled,
      sortOrder: item.sortOrder,
    }));
  }

  async saveCatalog(items: CatalogDraft[]): Promise<void> {
    await this.request("catalog", "PUT", catalogRequest(items));
  }

  async terminalStatus(): Promise<TerminalDiagnostic> {
    const payload = await this.request<unknown>("terminal/status");
    if (
      !isRecord(payload) ||
      typeof payload.connected !== "boolean" ||
      !isTerminalDiagnosticCode(payload.code) ||
      typeof payload.checkedAt !== "string" ||
      typeof payload.requestId !== "string" ||
      typeof payload.durationMs !== "number"
    ) {
      throw new Error("Worker returned an invalid terminal diagnostic.");
    }
    return {
      connected: payload.connected,
      code: payload.code,
      checkedAt: payload.checkedAt,
      requestId: payload.requestId,
      durationMs: payload.durationMs,
      ...(payload.adyenStatus === "ONLINE" || payload.adyenStatus === "OFFLINE"
        ? { adyenStatus: payload.adyenStatus }
        : {}),
      ...(typeof payload.upstreamStatus === "number"
        ? { upstreamStatus: payload.upstreamStatus }
        : {}),
      ...(typeof payload.adyenErrorCode === "string"
        ? { adyenErrorCode: payload.adyenErrorCode }
        : {}),
      ...(typeof payload.adyenRequestId === "string"
        ? { adyenRequestId: payload.adyenRequestId }
        : {}),
    };
  }

  private async request<T = unknown>(
    path: string,
    method = "GET",
    body?: unknown,
  ): Promise<T> {
    const response = await fetch(
      `/v1/installations/${encodeURIComponent(this.session.installationId)}/${path}`,
      {
        method,
        headers: {
          authorization: `Bearer ${this.session.installationSecret}`,
          ...(body === undefined ? {} : { "content-type": "application/json" }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      },
    );
    return readJson<T>(response);
  }
}

function configRequest(settings: RefundSettings): Record<string, unknown> {
  const apiKey = settings.apiKey.trim();
  const webhookHmacKey = settings.webhookHmacKey.trim();
  return {
    environment: settings.environment,
    liveRegion: settings.environment === "live" ? settings.region : null,
    merchantAccount: settings.merchantAccount.trim(),
    terminalId: settings.terminalId.trim(),
    saleId: settings.saleId.trim(),
    currency: settings.currency,
    maxRefundMinor: settings.maxRefundCents,
    paymentMethodId: settings.settlementRoute.trim(),
    ...(apiKey ? { apiKey } : {}),
    ...(webhookHmacKey ? { webhookHmacKey } : {}),
  };
}

function catalogRequest(items: CatalogDraft[]): { items: unknown[] } {
  return {
    items: items.map((item) => ({
      sku: item.sku.trim(),
      itemId: item.itemId.trim(),
      displayName: item.name.trim(),
      valueMinor: item.depositCents,
      enabled: item.enabled,
      sortOrder: item.sortOrder,
    })),
  };
}

async function readJson<T>(response: Response): Promise<T> {
  const payload: unknown = await response.json();
  if (!response.ok) {
    const error =
      isRecord(payload) && isRecord(payload.error) ? payload.error : null;
    throw new WorkerRequestError(
      typeof error?.message === "string"
        ? error.message
        : "Worker request failed",
      {
        status: response.status,
        code: typeof error?.code === "string" ? error.code : undefined,
        requestId:
          typeof error?.requestId === "string" ? error.requestId : undefined,
      },
    );
  }
  return payload as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isTerminalDiagnosticCode(
  value: unknown,
): value is TerminalDiagnosticCode {
  return (
    value === "online" ||
    value === "offline" ||
    value === "invalid_credentials" ||
    value === "insufficient_permissions" ||
    value === "terminal_not_found" ||
    value === "rate_limited" ||
    value === "adyen_unavailable" ||
    value === "adyen_error" ||
    value === "invalid_response" ||
    value === "network_error"
  );
}

function normalizeRegion(
  value: ConfigResponse["liveRegion"],
): RefundSettings["region"] {
  return value === "us" || value === "au" || value === "apse" ? value : "eu";
}
